import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { Pagination } from '@/components/ui/Pagination';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { ProductApi } from '@/api';
import { Plus, Edit, Trash2 } from 'lucide-react';

export default function Products() {
  const { push } = useToast();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 25;
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    category: 'machine' as any,
    description: '',
    hsn_sac: '',
    gst_rate: '',
    uom: '',
    lead_time_weeks: 0,
    min_price_inr: '',
    max_price_inr: '',
    min_price_usd: '',
    max_price_usd: '',
    min_price_eur: '',
    max_price_eur: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 whenever filters change
  useEffect(() => { setPage(1); }, [debouncedSearch, category]);

  const productsQuery = useQuery({
    queryKey: ['products', debouncedSearch, category, page],
    queryFn: () => ProductApi.list({ ...(debouncedSearch && { q: debouncedSearch }), ...(category && { category }), page, limit: LIMIT }),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => ProductApi.create(data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Product created' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to create product', body: err.message });
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => ProductApi.update(editId!, data),
    onSuccess: () => {
      push({ tone: 'success', title: 'Product updated' });
      setModalOpen(false);
      resetForm();
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to update product', body: err.message });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => ProductApi.deactivate(id),
    onSuccess: () => {
      push({ tone: 'success', title: 'Product deactivated' });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err: any) => {
      push({ tone: 'error', title: 'Failed to delete product', body: err.message });
    },
  });

  const resetForm = () => {
    setFormData({
      sku: '',
      name: '',
      category: 'machine',
      description: '',
      hsn_sac: '',
      gst_rate: '',
      uom: '',
      lead_time_weeks: 0,
      min_price_inr: '',
      max_price_inr: '',
      min_price_usd: '',
      max_price_usd: '',
      min_price_eur: '',
      max_price_eur: '',
    });
    setEditId(null);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.sku) newErrors.sku = 'SKU is required';
    if (!formData.name) newErrors.name = 'Name is required';
    if (!formData.gst_rate) newErrors.gst_rate = 'GST rate is required';
    if (!formData.uom) newErrors.uom = 'UOM is required';

    // Validate prices: either both or neither for each currency
    const currencies = ['inr', 'usd', 'eur'];
    currencies.forEach((curr) => {
      const min = formData[`min_price_${curr}` as keyof typeof formData];
      const max = formData[`max_price_${curr}` as keyof typeof formData];
      if ((min && !max) || (!min && max)) {
        newErrors[`price_${curr}`] = `Both min and max required for ${curr.toUpperCase()}`;
      }
      if (min && max && Number(min) > Number(max)) {
        newErrors[`price_${curr}`] = `Min cannot be greater than max`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleOpenModal = (product?: any) => {
    if (product) {
      setFormData({
        sku: product.sku,
        name: product.name,
        category: product.category,
        description: product.description,
        hsn_sac: product.hsn_sac || '',
        gst_rate: product.gst_rate,
        uom: product.uom,
        lead_time_weeks: product.lead_time_weeks,
        min_price_inr: product.min_price_inr || '',
        max_price_inr: product.max_price_inr || '',
        min_price_usd: product.min_price_usd || '',
        max_price_usd: product.max_price_usd || '',
        min_price_eur: product.min_price_eur || '',
        max_price_eur: product.max_price_eur || '',
      });
      setEditId(product.id);
    } else {
      resetForm();
    }
    setModalOpen(true);
  };

  const handleSave = () => {
    if (!validateForm()) return;
    const data = {
      sku: formData.sku,
      name: formData.name,
      category: formData.category,
      description: formData.description,
      hsn_sac: formData.hsn_sac || null,
      gst_rate: Number(formData.gst_rate),
      uom: formData.uom,
      lead_time_weeks: Number(formData.lead_time_weeks),
      min_price_inr: formData.min_price_inr ? Number(formData.min_price_inr) : null,
      max_price_inr: formData.max_price_inr ? Number(formData.max_price_inr) : null,
      min_price_usd: formData.min_price_usd ? Number(formData.min_price_usd) : null,
      max_price_usd: formData.max_price_usd ? Number(formData.max_price_usd) : null,
      min_price_eur: formData.min_price_eur ? Number(formData.min_price_eur) : null,
      max_price_eur: formData.max_price_eur ? Number(formData.max_price_eur) : null,
    };

    if (editId) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const productsPage = productsQuery.data || { data: [], total: 0, page: 1, limit: LIMIT };
  const filtered = productsPage.data;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Products"
        subtitle={isAdmin ? 'Manage your product catalog' : 'View the product catalog'}
        actions={
          isAdmin ? (
            <button
              className="btn btn-primary gap-2"
              onClick={() => handleOpenModal()}
            >
              <Plus size={16} /> Add Product
            </button>
          ) : undefined
        }
      />

      {/* Filters */}
      <div className="card card-pad flex flex-wrap gap-4 items-end">
        <div className="w-full sm:flex-1 sm:min-w-56">
          <label className="label">Search</label>
          <input
            type="text"
            className="input mt-1"
            placeholder="SKU, name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div>
          <label className="label">Category</label>
          <select
            className="input mt-1"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option value="">All</option>
            <option value="machine">Machine</option>
            <option value="consumable">Consumable</option>
            <option value="spare">Spare</option>
            <option value="service">Service</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {productsQuery.isLoading ? (
        <div className="text-center py-12 text-muted">Loading…</div>
      ) : filtered.length === 0 && productsPage.total === 0 ? (
        <EmptyState
          title="No products found"
          body={isAdmin ? 'Add your first product to get started.' : 'No products have been added yet.'}
          action={
            isAdmin ? (
              <button
                className="btn btn-primary"
                onClick={() => handleOpenModal()}
              >
                Add Product
              </button>
            ) : undefined
          }
        />
      ) : (
        <div className="card flex flex-col md:max-h-[calc(100vh-220px)]">
          <div className="overflow-auto flex-1">
            <table className="table-clean">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th>SKU</th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>GST Rate</th>
                  <th>Lead Time</th>
                  <th>Price Range (INR)</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p: any) => (
                  <tr key={p.id}>
                    <td className="font-medium">{p.sku}</td>
                    <td>{p.name}</td>
                    <td className="text-sm text-muted capitalize">{p.category}</td>
                    <td>{p.gst_rate}%</td>
                    <td>{p.lead_time_weeks} weeks</td>
                    <td className="text-sm">
                      {p.min_price_inr && p.max_price_inr
                        ? `₹${Number(p.min_price_inr).toFixed(0)} - ₹${Number(p.max_price_inr).toFixed(0)}`
                        : '-'}
                    </td>
                    {isAdmin && (
                      <td className="flex gap-1">
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => handleOpenModal(p)}
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => deleteMutation.mutate(p.id)}
                          disabled={deleteMutation.isPending}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-line px-4 shrink-0">
            <Pagination
              page={productsPage.page}
              total={productsPage.total}
              limit={productsPage.limit}
              onChange={setPage}
            />
          </div>
        </div>
      )}

      {/* Modal */}
      <Modal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        title={editId ? 'Edit Product' : 'Add Product'}
        footer={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              Save
            </button>
          </>
        }
      >
        <div className="space-y-3 max-h-96 overflow-y-auto">
          <div>
            <label className="label">SKU *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.sku ? 'input-error' : ''}`}
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            />
            {errors.sku && <div className="text-xs text-rdx mt-1">{errors.sku}</div>}
          </div>

          <div>
            <label className="label">Name *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.name ? 'input-error' : ''}`}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            />
            {errors.name && <div className="text-xs text-rdx mt-1">{errors.name}</div>}
          </div>

          <div>
            <label className="label">Category *</label>
            <select
              className="input mt-1"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              <option value="machine">Machine</option>
              <option value="consumable">Consumable</option>
              <option value="spare">Spare</option>
              <option value="service">Service</option>
            </select>
          </div>

          <div>
            <label className="label">Description</label>
            <textarea
              className="input mt-1"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          <div>
            <label className="label">HSN/SAC Code</label>
            <input
              type="text"
              className="input mt-1"
              value={formData.hsn_sac}
              onChange={(e) => setFormData({ ...formData, hsn_sac: e.target.value })}
            />
          </div>

          <div>
            <label className="label">GST Rate (%) *</label>
            <input
              type="number"
              className={`input mt-1 ${errors.gst_rate ? 'input-error' : ''}`}
              value={formData.gst_rate}
              onChange={(e) => setFormData({ ...formData, gst_rate: e.target.value })}
              step={0.01}
            />
            {errors.gst_rate && <div className="text-xs text-rdx mt-1">{errors.gst_rate}</div>}
          </div>

          <div>
            <label className="label">UOM *</label>
            <input
              type="text"
              className={`input mt-1 ${errors.uom ? 'input-error' : ''}`}
              value={formData.uom}
              onChange={(e) => setFormData({ ...formData, uom: e.target.value })}
              placeholder="e.g. Piece, KG, Meter"
            />
            {errors.uom && <div className="text-xs text-rdx mt-1">{errors.uom}</div>}
          </div>

          <div>
            <label className="label">Lead Time (Weeks)</label>
            <input
              type="number"
              className="input mt-1"
              value={formData.lead_time_weeks}
              onChange={(e) => setFormData({ ...formData, lead_time_weeks: Number(e.target.value) })}
              min={0}
            />
          </div>

          <div className="border-t border-line pt-3">
            <h4 className="text-sm font-medium mb-2">Price Range (INR)</h4>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="label">Min</label>
                <input
                  type="number"
                  className={`input mt-1 ${errors.price_inr ? 'input-error' : ''}`}
                  value={formData.min_price_inr}
                  onChange={(e) => setFormData({ ...formData, min_price_inr: e.target.value })}
                />
              </div>
              <div className="flex-1">
                <label className="label">Max</label>
                <input
                  type="number"
                  className={`input mt-1 ${errors.price_inr ? 'input-error' : ''}`}
                  value={formData.max_price_inr}
                  onChange={(e) => setFormData({ ...formData, max_price_inr: e.target.value })}
                />
              </div>
            </div>
            {errors.price_inr && <div className="text-xs text-rdx mt-1">{errors.price_inr}</div>}
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">Price Range (USD)</h4>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="label">Min</label>
                <input
                  type="number"
                  className={`input mt-1 ${errors.price_usd ? 'input-error' : ''}`}
                  value={formData.min_price_usd}
                  onChange={(e) => setFormData({ ...formData, min_price_usd: e.target.value })}
                />
              </div>
              <div className="flex-1">
                <label className="label">Max</label>
                <input
                  type="number"
                  className={`input mt-1 ${errors.price_usd ? 'input-error' : ''}`}
                  value={formData.max_price_usd}
                  onChange={(e) => setFormData({ ...formData, max_price_usd: e.target.value })}
                />
              </div>
            </div>
            {errors.price_usd && <div className="text-xs text-rdx mt-1">{errors.price_usd}</div>}
          </div>

          <div>
            <h4 className="text-sm font-medium mb-2">Price Range (EUR)</h4>
            <div className="flex gap-2">
              <div className="flex-1">
                <label className="label">Min</label>
                <input
                  type="number"
                  className={`input mt-1 ${errors.price_eur ? 'input-error' : ''}`}
                  value={formData.min_price_eur}
                  onChange={(e) => setFormData({ ...formData, min_price_eur: e.target.value })}
                />
              </div>
              <div className="flex-1">
                <label className="label">Max</label>
                <input
                  type="number"
                  className={`input mt-1 ${errors.price_eur ? 'input-error' : ''}`}
                  value={formData.max_price_eur}
                  onChange={(e) => setFormData({ ...formData, max_price_eur: e.target.value })}
                />
              </div>
            </div>
            {errors.price_eur && <div className="text-xs text-rdx mt-1">{errors.price_eur}</div>}
          </div>
        </div>
      </Modal>
    </div>
  );
}
